import { FC } from 'react';
import { Box } from '@mui/material';
import { FormikProps } from 'formik';

import CustomInput from '../../../../../../../common/CustomInput';
import CustomReactDatePicker from '../../../../../../../common/CustomReactDatePicker';
import CustomTimePicker from '../../../../../../../common/CustomTimePicker';
import { CreateActivityFormData } from '../../../../../../../types/activity.types';

import styles from '../../CreateActivityModal.module.scss';
import { useFormFieldRenderer } from '../../hooks/useFormFieldRenderer';
import { getMinEndTimeForSameDay } from '../../../../../../../utils/dateUtils';

const STOP_DATE_PICKER_PROPS = {
  dateFormat: 'MMM d',
  iconPosition: 'right' as const,
};

const STOP_TIME_PICKER_PROPS = {
  iconPosition: 'right' as const,
  timeDisplayFormat: 'hh:mm a',
};

interface MandatoryFieldsProps {
  formik: FormikProps<CreateActivityFormData>;
  tripMinDate?: string;
  tripMaxDate?: string;
}

const MandatoryFields: FC<MandatoryFieldsProps> = ({ formik, tripMinDate, tripMaxDate }) => {
  const { values, handleChange } = formik;
  const activityType = values.activityType;

  const { isFieldVisible, isFieldMandatory, getError, renderFieldInput } = useFormFieldRenderer(formik, activityType);
  const todayIso = new Date().toISOString().split('T')[0];
  const baseMinDate = tripMinDate && tripMinDate > todayIso ? tripMinDate : todayIso;
  const tripBoundedMin = (candidate?: string) => (candidate && candidate > baseMinDate ? candidate : baseMinDate);
  const tripBoundedMax = tripMaxDate;

  return (
    <Box className={styles.formSection}>
      {isFieldVisible('name') && renderFieldInput('name', 'Name')}
      {isFieldVisible('vehicleType') && isFieldMandatory('vehicleType') && (
        <CustomInput
          inputName="vehicleType"
          inputLabel="Vehicle Type"
          value={values.vehicleType || ''}
          onChange={handleChange}
          error={getError('vehicleType')}
          placeholder="Enter vehicle type (e.g., Bus, Train, Car, Taxi)"
        />
      )}
      {isFieldVisible('place') && isFieldMandatory('place') && renderFieldInput('place', 'Place')}
      {isFieldVisible('flightFrom') && renderFieldInput('flightFrom', 'Flight From')}
      {isFieldVisible('flightTo') && renderFieldInput('flightTo', 'Flight To')}
      {isFieldVisible('departureDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="departureDate"
            inputLabel="Departure Date"
            placeholder="Select departure date"
            value={values.departureDate || ''}
            onChange={handleChange}
            error={!!getError('departureDate')}
            helperText={getError('departureDate')}
            min={baseMinDate}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="departureTime"
            inputLabel="Departure Time"
            placeholder="Select departure time"
            value={values.departureTime || ''}
            onChange={handleChange}
            error={!!getError('departureTime')}
            helperText={getError('departureTime')}
          />
        </Box>
      )}

      {isFieldVisible('arrivalDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="arrivalDate"
            inputLabel="Arrival Date"
            placeholder="Select arrival date"
            value={values.arrivalDate}
            onChange={handleChange}
            error={!!getError('arrivalDate')}
            helperText={getError('arrivalDate')}
            min={tripBoundedMin(values.departureDate)}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="arrivalTime"
            inputLabel="Arrival Time"
            placeholder="Select arrival time"
            value={values.arrivalTime}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(
              values.departureDate,
              values.arrivalDate,
              values.departureTime
            )}
            error={!!getError('arrivalTime')}
            helperText={getError('arrivalTime')}
          />
        </Box>
      )}

      {isFieldVisible('checkInDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="checkInDate"
            inputLabel="Check-in Date"
            placeholder="Select check-in date"
            value={values.checkInDate}
            onChange={handleChange}
            error={!!getError('checkInDate')}
            helperText={getError('checkInDate')}
            min={baseMinDate}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="checkInTime"
            inputLabel="Check-in Time"
            placeholder="Select check-in time"
            value={values.checkInTime}
            onChange={handleChange}
            error={!!getError('checkInTime')}
            helperText={getError('checkInTime')}
          />
        </Box>
      )}

      {isFieldVisible('checkOutDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="checkOutDate"
            inputLabel="Check-out Date"
            placeholder="Select check-out date"
            value={values.checkOutDate}
            onChange={handleChange}
            error={!!getError('checkOutDate')}
            helperText={getError('checkOutDate')}
            min={tripBoundedMin(values.checkInDate)}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="checkOutTime"
            inputLabel="Check-out Time"
            placeholder="Select check-out time"
            value={values.checkOutTime}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(values.checkInDate, values.checkOutDate, values.checkInTime)}
            error={!!getError('checkOutTime')}
            helperText={getError('checkOutTime')}
          />
        </Box>
      )}

      {isFieldVisible('startDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="startDate"
            inputLabel="Start Date"
            placeholder="Select start date"
            value={values.startDate}
            onChange={handleChange}
            error={!!getError('startDate')}
            helperText={getError('startDate')}
            min={baseMinDate}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="startTime"
            inputLabel="Start Time"
            placeholder="Select start time"
            value={values.startTime}
            onChange={handleChange}
            error={!!getError('startTime')}
            helperText={getError('startTime')}
          />
        </Box>
      )}

      {isFieldVisible('endDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="endDate"
            inputLabel="End Date"
            placeholder="Select end date"
            value={values.endDate}
            onChange={handleChange}
            error={!!getError('endDate')}
            helperText={getError('endDate')}
            min={tripBoundedMin(values.startDate)}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="endTime"
            inputLabel="End Time"
            placeholder="Select end time"
            value={values.endTime}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(values.startDate, values.endDate, values.startTime)}
            error={!!getError('endTime')}
            helperText={getError('endTime')}
          />
        </Box>
      )}

      {isFieldVisible('date') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            {...STOP_DATE_PICKER_PROPS}
            name="date"
            inputLabel="Date"
            placeholder="Select date"
            value={values.date}
            onChange={handleChange}
            error={!!getError('date')}
            helperText={getError('date')}
            min={baseMinDate}
            max={tripBoundedMax}
          />
          <CustomTimePicker
            {...STOP_TIME_PICKER_PROPS}
            name="time"
            inputLabel="Time"
            placeholder="Select time"
            value={values.time}
            onChange={handleChange}
            error={!!getError('time')}
            helperText={getError('time')}
          />
        </Box>
      )}
      {isFieldVisible('country_from') && renderFieldInput('country_from', 'Country From')}
      {isFieldVisible('country_to') && renderFieldInput('country_to', 'Country To')}
    </Box>
  );
};

export default MandatoryFields;
